<template>
  <RevLoadingScreen
    v-if="isLoading && !cart"
    class="bg-surface-default-mid fixed inset-0"
    data-qa="loading-screen"
    :text="''"
  />
  <slot
    v-if="cart"
    :addressList="cart.addressList"
    :billableCountries="cart.billableCountries"
    :items="cart.cartItems"
    :lastCollectionPoint="cart.lastCollectionPoint"
    :loading="isLoading"
    :shippableCountries="cart.shippableCountries"
    :summary="cart.summary"
    @update="handleUpdate"
    @updating="handleUpdating"
  />
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import type { CartItem } from '~/api/cart/types'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { useSwapStore } from '~/scopes/checkout/stores/swapStore'
import { useUserInformationStore } from '~/scopes/checkout/stores/userInformationStore'

const $httpFetch = useHttpFetch()
const experiments = useExperiments()
const { setSwap } = useSwapStore()
const { setUserInformation } = useUserInformationStore()
const { trackFunnel } = useCheckoutTracking()

const newPriceGrid =
  experiments['experiment.serviceFee2025'] === 'newServiceFeeGrid25'

const includeBackupPlusOffer =
  experiments['experiment.subscriptionsOnCart'] === 'withBackUpPlus'

type Cart = ApiSpecs['/bm/cart']['GET']['response']

const isLoading = ref(false)
const cart = ref<Cart | null>(null)

async function fetchCart() {
  try {
    const response = await $httpFetch('/bm/cart', {
      query: {
        newPriceGrid,
        includeBackupPlusOffer,
      },
    })
    const responseCamelized = camelizeKeys(response)
    setSwap(responseCamelized.swap)
    setUserInformation(responseCamelized.userInformation)

    return responseCamelized
  } catch (error) {
    return null
  }
}

onMounted(async () => {
  isLoading.value = true
  cart.value = await fetchCart()
  isLoading.value = false
})

/**
 * Applies an optimistic update to the cart items by replacing the item with matching listingId
 * @param currentCartItems - The current array of cart items
 * @param itemToUpdate - The updated cart item to replace in the array
 * @returns A new array with the updated item replaced
 */
function applyOptimisticUpdate(
  cartItems: CartItem[],
  updatedItem: CartItem,
): CartItem[] {
  return cartItems.reduce<CartItem[]>((items, item) => {
    if (item.listingId === updatedItem.listingId) {
      return [...items, updatedItem]
    }

    return [...items, item]
  }, [])
}

async function handleUpdating(updatedItem?: CartItem) {
  const hasOptimisticUpdate = !!updatedItem

  // Set loading state only if we're not doing optimistic updates
  if (!hasOptimisticUpdate) {
    isLoading.value = true
  }

  // Apply optimistic update to immediately reflect changes in the UI
  if (hasOptimisticUpdate && cart.value && updatedItem) {
    cart.value.cartItems = applyOptimisticUpdate(
      cart.value.cartItems,
      updatedItem,
    )
  }
}

async function handleUpdate(callback?: () => void) {
  const updatedCart = await fetchCart()

  if (updatedCart) {
    cart.value = updatedCart
    trackFunnel(updatedCart.cartItems, updatedCart.swap.offer)
  }

  isLoading.value = false

  if (callback) {
    callback()
  }
}
</script>
